import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

// Persists the "acting on behalf of team X" mode across client-side navigation
// inside the in-app WebView and ordinary browser sessions. SAB-341 requires
// the banner to remain visible as the commissioner navigates between roster /
// lineup / trades — without persistence, a route change would silently exit
// commissioner mode and the next mutation would be mis-attributed.
//
// Stored in sessionStorage (one-tab, one-app-session) rather than localStorage
// so a commissioner cannot accidentally inherit the mode in a future session.

interface ActAsState {
  actAsTeamId: string | null;
  actAsTeamName: string | null;
  actAsLeagueId: string | null;
}

interface CommissionerActAsContextValue extends ActAsState {
  setActAsTeam: (params: { teamId: string; teamName: string; leagueId: string }) => void;
  exitMode: () => void;
  isActingAs: boolean;
}

const STORAGE_KEY = 'sf_commissioner_act_as';

const CommissionerActAsContext = createContext<CommissionerActAsContextValue | null>(null);

function readStoredState(): ActAsState {
  if (typeof window === 'undefined') {
    return { actAsTeamId: null, actAsTeamName: null, actAsLeagueId: null };
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { actAsTeamId: null, actAsTeamName: null, actAsLeagueId: null };
    const parsed = JSON.parse(raw);
    return {
      actAsTeamId: typeof parsed?.actAsTeamId === 'string' ? parsed.actAsTeamId : null,
      actAsTeamName: typeof parsed?.actAsTeamName === 'string' ? parsed.actAsTeamName : null,
      actAsLeagueId: typeof parsed?.actAsLeagueId === 'string' ? parsed.actAsLeagueId : null,
    };
  } catch {
    return { actAsTeamId: null, actAsTeamName: null, actAsLeagueId: null };
  }
}

export function CommissionerActAsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ActAsState>(readStoredState);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (state.actAsTeamId) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch { /* private mode / quota — silently no-op */ }
  }, [state]);

  const setActAsTeam = useCallback(
    ({ teamId, teamName, leagueId }: { teamId: string; teamName: string; leagueId: string }) => {
      setState({ actAsTeamId: teamId, actAsTeamName: teamName, actAsLeagueId: leagueId });
    },
    [],
  );

  const exitMode = useCallback(() => {
    setState({ actAsTeamId: null, actAsTeamName: null, actAsLeagueId: null });
  }, []);

  const value: CommissionerActAsContextValue = {
    ...state,
    setActAsTeam,
    exitMode,
    isActingAs: !!state.actAsTeamId,
  };

  return (
    <CommissionerActAsContext.Provider value={value}>
      {children}
    </CommissionerActAsContext.Provider>
  );
}

export function useCommissionerActAs(): CommissionerActAsContextValue {
  const ctx = useContext(CommissionerActAsContext);
  if (!ctx) {
    throw new Error('useCommissionerActAs must be used within CommissionerActAsProvider');
  }
  return ctx;
}
